import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { ProjectPhase } from "@/hooks/useClientProjects";
import { jiraProgress, phaseProgress } from "@/lib/deliveryProgress";
import { formatBRL } from "@/lib/format";

// ─── Delivery-tracking aggregation for the "Projetos" admin module ──────────
//
// This is a separate hook file rather than an addition to useClientProjects.ts
// on purpose: useClientProjects.ts backs the legacy Kanban (phase/display_order
// mutations) and is read by several other screens (LeadDetail, Pipeline,
// SalesDashboard) — bloating it with progress/invoicing joins that only the
// new Projetos screens need would couple unrelated concerns. See useDeliveryProjects()
// below for exactly what's joined and why.

export interface DeliveryProject {
  id: string;
  title: string;
  description: string | null;
  client_name: string | null;
  client_email: string | null;
  lead_id: string | null;
  phase: ProjectPhase;
  due_date: string | null;
  uses_jira_mode: boolean;
  assigned_to: string | null;
  estimated_hours: number | null;
  logged_hours: number | null;
  created_at: string;
  updated_at: string;
  /**
   * 0–100, rounded. `null` when it genuinely cannot be computed (Jira-mode
   * project with zero backlog items yet) — render an honest "—", never a
   * fabricated number.
   */
  progress: number | null;
  progressBasis: "jira" | "phase";
  /** True once at least one budget (any status) is linked to this project. */
  hasBudget: boolean;
  /** Sum of net_total for this project's signed/accepted budgets (BRL). */
  contractedAmount: number;
  /** Sum of payments.net (fallback amount) against any budget linked to this project (BRL). */
  invoicedAmount: number;
  openBacklogCount: number;
  totalBacklogCount: number;
}

type ClientProjectRow = {
  id: string;
  title: string;
  description: string | null;
  client_name: string | null;
  client_email: string | null;
  lead_id: string | null;
  phase: ProjectPhase | null;
  due_date: string | null;
  uses_jira_mode: boolean;
  assigned_to: string | null;
  estimated_hours: number | null;
  logged_hours: number | null;
  created_at: string | null;
  updated_at: string | null;
};

type BacklogItemRow = { id: string; client_project_id: string; status: string };
type SubtaskRow = { id: string; backlog_item_id: string; is_done: boolean };
type BudgetRow = {
  id: string;
  client_project_id: string | null;
  status: string | null;
  net_total: number;
};
type PaymentRow = { budget_id: string | null; amount: number; net: number | null };

// jiraProgress()/phaseProgress() live in src/lib/deliveryProgress.ts (pure,
// unit-tested there) — imported above.

export function useDeliveryProjects() {
  return useQuery({
    queryKey: ["delivery-projects"],
    queryFn: async (): Promise<DeliveryProject[]> => {
      const { data: projectRows, error: projectsError } = await supabase
        .from("client_projects")
        .select(
          "id, title, description, client_name, client_email, lead_id, phase, due_date, uses_jira_mode, assigned_to, estimated_hours, logged_hours, created_at, updated_at",
        )
        .order("updated_at", { ascending: false });
      if (projectsError) throw projectsError;
      const projects = (projectRows ?? []) as unknown as ClientProjectRow[];
      if (projects.length === 0) return [];

      const projectIds = projects.map((p) => p.id);

      const [
        { data: backlogRows, error: backlogError },
        { data: budgetRows, error: budgetsError },
      ] = await Promise.all([
        supabase
          .from("project_backlog_items" as never)
          .select("id, client_project_id, status")
          .in("client_project_id", projectIds),
        supabase
          .from("budgets" as never)
          .select("id, client_project_id, status, net_total")
          .in("client_project_id", projectIds),
      ]);
      if (backlogError) throw backlogError;
      if (budgetsError) throw budgetsError;

      const backlogItems = (backlogRows ?? []) as unknown as BacklogItemRow[];
      const budgets = (budgetRows ?? []) as unknown as BudgetRow[];

      const backlogItemIds = backlogItems.map((b) => b.id);
      const { data: subtaskRows, error: subtasksError } =
        backlogItemIds.length > 0
          ? await supabase
              .from("project_subtasks" as never)
              .select("id, backlog_item_id, is_done")
              .in("backlog_item_id", backlogItemIds)
          : { data: [] as SubtaskRow[], error: null };
      if (subtasksError) throw subtasksError;
      const subtasks = (subtaskRows ?? []) as unknown as SubtaskRow[];

      const budgetIds = budgets.map((b) => b.id);
      const { data: paymentRows, error: paymentsError } =
        budgetIds.length > 0
          ? await supabase
              .from("payments" as never)
              .select("budget_id, amount, net")
              .in("budget_id", budgetIds)
          : { data: [] as PaymentRow[], error: null };
      if (paymentsError) throw paymentsError;
      const payments = (paymentRows ?? []) as unknown as PaymentRow[];

      // ── group helpers ──
      const backlogByProject = new Map<string, BacklogItemRow[]>();
      for (const item of backlogItems) {
        const list = backlogByProject.get(item.client_project_id) ?? [];
        list.push(item);
        backlogByProject.set(item.client_project_id, list);
      }
      const subtasksByItem = new Map<string, SubtaskRow[]>();
      for (const s of subtasks) {
        const list = subtasksByItem.get(s.backlog_item_id) ?? [];
        list.push(s);
        subtasksByItem.set(s.backlog_item_id, list);
      }
      const budgetsByProject = new Map<string, BudgetRow[]>();
      for (const b of budgets) {
        if (!b.client_project_id) continue;
        const list = budgetsByProject.get(b.client_project_id) ?? [];
        list.push(b);
        budgetsByProject.set(b.client_project_id, list);
      }
      const paymentsByBudget = new Map<string, PaymentRow[]>();
      for (const p of payments) {
        if (!p.budget_id) continue;
        const list = paymentsByBudget.get(p.budget_id) ?? [];
        list.push(p);
        paymentsByBudget.set(p.budget_id, list);
      }

      return projects.map((p): DeliveryProject => {
        const items = backlogByProject.get(p.id) ?? [];
        const progress = p.uses_jira_mode
          ? jiraProgress(items, subtasksByItem)
          : phaseProgress(p.phase);

        const projectBudgets = budgetsByProject.get(p.id) ?? [];
        const contractedAmount = projectBudgets
          .filter((b) => b.status === "signed" || b.status === "accepted")
          .reduce((sum, b) => sum + Number(b.net_total || 0), 0);
        const invoicedAmount = projectBudgets.reduce((sum, b) => {
          const pays = paymentsByBudget.get(b.id) ?? [];
          return sum + pays.reduce((s, pay) => s + Number(pay.net ?? pay.amount ?? 0), 0);
        }, 0);

        return {
          id: p.id,
          title: p.title,
          description: p.description,
          client_name: p.client_name,
          client_email: p.client_email,
          lead_id: p.lead_id,
          phase: p.phase ?? "backlog",
          due_date: p.due_date,
          uses_jira_mode: p.uses_jira_mode,
          assigned_to: p.assigned_to,
          estimated_hours: p.estimated_hours,
          logged_hours: p.logged_hours,
          created_at: p.created_at ?? new Date(0).toISOString(),
          updated_at: p.updated_at ?? new Date(0).toISOString(),
          progress,
          progressBasis: p.uses_jira_mode ? "jira" : "phase",
          hasBudget: projectBudgets.length > 0,
          contractedAmount,
          invoicedAmount,
          openBacklogCount: items.filter((i) => i.status !== "done").length,
          totalBacklogCount: items.length,
        };
      });
    },
  });
}

const PHASE_LABELS: Record<ProjectPhase, string> = {
  backlog: "Backlog",
  in_progress: "Em Progresso",
  review: "Revisão",
  done: "Concluído",
};

export type DeliveryStatusTone = "neutral" | "warn" | "ok";

/**
 * A project's status badge label/tone. Jira-mode projects don't reliably
 * maintain `phase` (the Jira workspace never writes back to it — confirmed
 * by reading src/components/admin/projects/*.tsx), so their badge is derived
 * from the same `progress` this hook already computed. Legacy projects use
 * their real `phase` field directly, which preserves the "Revisão" state
 * that a progress-only view would otherwise collapse into "Em andamento".
 */
export function deliveryStatus(project: DeliveryProject): {
  label: string;
  tone: DeliveryStatusTone;
} {
  if (!project.uses_jira_mode) {
    return {
      label: PHASE_LABELS[project.phase],
      tone: project.phase === "done" ? "ok" : "neutral",
    };
  }
  if (project.progress === null) return { label: "Sem itens", tone: "neutral" };
  if (project.progress === 100) return { label: "Concluído", tone: "ok" };
  if (project.progress === 0) return { label: "A iniciar", tone: "neutral" };
  return { label: "Em andamento", tone: "warn" };
}

/**
 * The progress percentage as text, prefixed with "≈" when it came from
 * `phaseProgress()` (a coarse 0/33/67/100 estimate off the legacy kanban
 * phase) rather than `jiraProgress()` (measured from actual backlog
 * item/subtask completion). Without this, a legacy project parked in
 * "review" (67%) would render pixel-identical to a Jira-mode project with
 * 67% of subtasks genuinely done — the false-precision problem `progressBasis`
 * exists to prevent. Use alongside `progressCaption()` wherever progress is
 * displayed.
 */
export function progressLabel(project: DeliveryProject): string {
  if (project.progress === null) return "—";
  return project.progressBasis === "phase" ? `≈${project.progress}%` : `${project.progress}%`;
}

/** Short explanation of what progressLabel()'s value actually measures. Null when there's nothing to caption (progress is null). */
export function progressCaption(project: DeliveryProject): string | null {
  if (project.progress === null) return null;
  return project.progressBasis === "phase"
    ? "Estimado pela fase do Kanban, não medido item a item"
    : "Medido pelos itens do backlog";
}

// ─── Per-project timeline (DeliveryProjectDetail's "Linha do tempo") ────────
//
// There's no dedicated milestones table, so this timeline is built entirely
// from real, already-timestamped events on this project: its own creation,
// its budgets being signed, payments being received, and — for Jira-mode
// projects — sprint start/end dates. Nothing here is invented; a project
// with no budget/payment/sprint activity yet simply gets a short timeline
// (just "Projeto criado" + due date, if any) rather than padded-out fake
// steps.

export type TimelineEventKind = "created" | "budget_signed" | "payment" | "sprint" | "due";

export interface TimelineEvent {
  date: string;
  label: string;
  kind: TimelineEventKind;
}

interface DeliveryDetailBudgetRow {
  id: string;
  status: string | null;
  net_total: number;
  finalized_at: string | null;
}
interface DeliveryDetailPaymentRow {
  amount: number;
  net: number | null;
  received_at: string;
}
interface DeliveryDetailSprintRow {
  id: string;
  name: string;
  status: string;
  start_date: string | null;
  end_date: string | null;
}

export function useDeliveryProjectTimeline(project: DeliveryProject | undefined) {
  return useQuery({
    queryKey: ["delivery-project-timeline", project?.id],
    queryFn: async (): Promise<TimelineEvent[]> => {
      if (!project) return [];
      const events: TimelineEvent[] = [
        { date: project.created_at, label: "Projeto criado", kind: "created" },
      ];

      const { data: budgetRows, error: budgetsError } = await supabase
        .from("budgets" as never)
        .select("id, status, net_total, finalized_at")
        .eq("client_project_id", project.id);
      if (budgetsError) throw budgetsError;
      const budgets = (budgetRows ?? []) as unknown as DeliveryDetailBudgetRow[];

      for (const b of budgets) {
        if (b.finalized_at && (b.status === "signed" || b.status === "accepted")) {
          events.push({
            date: b.finalized_at,
            label: `Orçamento assinado — ${formatBRL(Number(b.net_total || 0))}`,
            kind: "budget_signed",
          });
        }
      }

      const budgetIds = budgets.map((b) => b.id);
      if (budgetIds.length > 0) {
        const { data: paymentRows, error: paymentsError } = await supabase
          .from("payments" as never)
          .select("amount, net, received_at")
          .in("budget_id", budgetIds);
        if (paymentsError) throw paymentsError;
        const payments = (paymentRows ?? []) as unknown as DeliveryDetailPaymentRow[];
        for (const p of payments) {
          events.push({
            date: p.received_at,
            label: `Pagamento recebido — ${formatBRL(Number(p.net ?? p.amount ?? 0))}`,
            kind: "payment",
          });
        }
      }

      if (project.uses_jira_mode) {
        const { data: sprintRows, error: sprintsError } = await supabase
          .from("project_sprints" as never)
          .select("id, name, status, start_date, end_date")
          .eq("client_project_id", project.id);
        if (sprintsError) throw sprintsError;
        const sprints = (sprintRows ?? []) as unknown as DeliveryDetailSprintRow[];
        for (const s of sprints) {
          if (s.start_date)
            events.push({
              date: s.start_date,
              label: `Sprint "${s.name}" iniciada`,
              kind: "sprint",
            });
          if (s.status === "completed" && s.end_date) {
            events.push({
              date: s.end_date,
              label: `Sprint "${s.name}" concluída`,
              kind: "sprint",
            });
          }
        }
      }

      if (project.due_date) {
        events.push({ date: project.due_date, label: "Entrega prevista", kind: "due" });
      }

      return events.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    },
    enabled: !!project,
  });
}
